'use client';

import React, { useState, useEffect, useCallback } from 'react';
import ReportScamForm from './ReportScamForm';
import { createClient } from '@/lib/local-api/client';
import { scamAlertsApi } from '@/lib/api';
import SignInPromptModal from '@/components/SignInPromptModal';
import { ExclamationTriangleIcon, ShieldExclamationIcon, FunnelIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, UserGroupIcon, CurrencyDollarIcon, GlobeAltIcon, ExclamationCircleIcon,  } from '@heroicons/react/24/outline';

const tabFilters = ['All Alerts', 'Scam Warnings', 'High Risk', 'Reports'];
const severityFilters = ['All', 'Critical', 'High', 'Medium'];

interface ScamAlert {
  id: string;
  projectName: string;
  alertType: string;
  severity: 'Critical' | 'High' | 'Medium';
  status: 'Active' | 'Under Investigation' | 'Confirmed' | 'Archived';
  reportedDate: string;
  reporterCount: number;
  estimatedDamage: string;
  affectedCountries: string[];
  description: string;
  verified: boolean;
  category: string;
}

function mapRow(row: any): ScamAlert {
  return {
    id: row.id,
    projectName: row.project_name,
    alertType: row.alert_type,
    severity: row.severity,
    status: row.report_status,
    reportedDate: new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    reporterCount: row.reporter_count ?? 1,
    estimatedDamage: row.estimated_damage ?? 'Unknown',
    affectedCountries: row.affected_countries ?? [],
    description: row.description,
    verified: row.verified ?? false,
    category: row.category ?? '',
  };
}

function AlertCard({ alert }: { alert: ScamAlert }) {
  const [expanded, setExpanded] = useState(false);

  const severityConfig = {
    Critical: {
      bg: 'bg-red-500/12',
      border: 'border-red-500/25',
      badge: 'bg-gradient-to-r from-red-600 to-red-500 text-white',
      dot: 'bg-red-500',
      icon: 'text-red-400',
      iconBg: 'bg-red-500/15 border border-red-500/20',
      glow: '0 0 0 1px rgba(239,68,68,0.2), 0 4px 24px rgba(239,68,68,0.08)',
      label: 'Critical',
    },
    High: {
      bg: 'bg-orange-500/8',
      border: 'border-orange-500/20',
      badge: 'bg-gradient-to-r from-orange-600 to-orange-500 text-white',
      dot: 'bg-orange-500',
      icon: 'text-orange-400',
      iconBg: 'bg-orange-500/15 border border-orange-500/20',
      glow: '0 0 0 1px rgba(249,115,22,0.2), 0 4px 24px rgba(249,115,22,0.06)',
      label: 'High Risk',
    },
    Medium: {
      bg: 'bg-yellow-500/8',
      border: 'border-yellow-500/20',
      badge: 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black',
      dot: 'bg-yellow-500',
      icon: 'text-yellow-400',
      iconBg: 'bg-yellow-500/15 border border-yellow-500/20',
      glow: '0 0 0 1px rgba(245,158,11,0.2), 0 4px 24px rgba(245,158,11,0.06)',
      label: 'Medium',
    },
  };

  const config = severityConfig[alert.severity] || severityConfig.Medium;

  return (
    <div
      className={`bg-card border ${config.border} rounded-2xl overflow-hidden transition-all duration-300`}
      style={{ boxShadow: config.glow }}
    >
      <div
        className={`flex items-start gap-3 p-4 cursor-pointer transition-colors hover:bg-white/[0.02]`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-10 h-10 rounded-xl ${config.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <ExclamationTriangleIcon style={{ width: 18, height: 18 }} className={config.icon} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-sm font-bold text-foreground leading-tight">{alert.projectName}</p>
            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${config.badge}`}
              style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.2)' }}
            >
              {config.label}
            </span>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-1 leading-relaxed">{alert.description}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <UserGroupIcon style={{ width: 11, height: 11 }} />
              {alert.reporterCount} reports
            </span>
            <span className="text-[10px] text-muted-foreground/60">·</span>
            <span className="text-[10px] text-muted-foreground">{alert.reportedDate}</span>
            {alert.verified && (
              <>
                <span className="text-[10px] text-muted-foreground/60">·</span>
                <span className="flex items-center gap-0.5 text-[10px] text-green-400 font-semibold">
                  <ShieldCheckIcon style={{ width: 10, height: 10 }} />
                  Verified
                </span>
              </>
            )}
          </div>
        </div>

        <button className="text-muted-foreground/50 flex-shrink-0 mt-1 hover:text-muted-foreground transition-colors">
          {expanded ? (
            <ChevronUpIcon style={{ width: 14, height: 14 }} />
          ) : (
            <ChevronDownIcon style={{ width: 14, height: 14 }} />
          )}
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-0 space-y-3 border-t border-border/50 animate-slide-up">
          <p className="text-sm text-muted-foreground leading-relaxed pt-3">{alert.description}</p>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-500/10 border border-red-500/15">
              <CurrencyDollarIcon className="text-red-400" style={{ width: 13, height: 13 }} />
              <span className="text-xs font-semibold text-red-400">{alert.estimatedDamage} est. damage</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-lg border ${
                alert.status === 'Active' ? 'text-red-400 bg-red-500/10 border-red-500/20' :
                alert.status === 'Confirmed' ? 'text-orange-400 bg-orange-500/10 border-orange-500/20' : 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20'
              }`}>
                {alert.status}
              </span>
            </div>
          </div>
          {(alert.affectedCountries?.length ?? 0) > 0 && (
            <div className="flex items-start gap-2">
              <GlobeAltIcon className="text-muted-foreground flex-shrink-0 mt-0.5" style={{ width: 13, height: 13 }} />
              <div className="flex flex-wrap gap-1.5">
                {alert.affectedCountries?.slice(0, 4).map((country) => (
                  <span key={`${alert.id}-${country}`} className="text-[10px] px-2 py-0.5 rounded-full bg-muted/80 text-muted-foreground border border-border/50">
                    {country}
                  </span>
                ))}
                {(alert.affectedCountries?.length ?? 0) > 4 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    +{alert.affectedCountries.length - 4} more
                  </span>
                )}
              </div>
            </div>
          )}
          {!alert.verified && (
            <div className="flex items-center gap-2 p-2.5 bg-yellow-500/5 border border-yellow-500/15 rounded-xl">
              <ExclamationCircleIcon className="text-yellow-400 flex-shrink-0" style={{ width: 13, height: 13 }} />
              <p className="text-[11px] text-yellow-400/90">Unverified community report — under investigation.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ScamAlertContent() {
  const [activeTab, setActiveTab] = useState('All Alerts');
  const [severity, setSeverity] = useState('All');
  const [search, setSearch] = useState('');
  const [showReportForm, setShowReportForm] = useState(false);
  const [alerts, setAlerts] = useState<ScamAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  const fetchAlerts = useCallback(async () => {
    const { data, error } = await scamAlertsApi.list();
    if (!error && data) {
      const rows = Array.isArray(data) ? data : (data as any).data ?? [];
      setAlerts(rows.map(mapRow));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAlerts();

    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUser(data?.user ?? null);
    });

    const channel = supabase
      .channel('scam_reports_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'scam_reports' }, () => {
        fetchAlerts();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchAlerts]);

  const handleReportClick = () => {
    if (!currentUser) {
      setShowSignInPrompt(true);
    } else {
      setShowReportForm(true);
    }
  };

  const filtered = alerts?.filter((a) => {
    const matchSev = severity === 'All' || a?.severity === severity;
    const matchSearch = search === '' || a?.projectName?.toLowerCase()?.includes(search?.toLowerCase());
    const matchTab = activeTab === 'All Alerts' ||
      (activeTab === 'High Risk' && (a?.severity === 'Critical' || a?.severity === 'High')) ||
      (activeTab === 'Scam Warnings' && a?.status === 'Active') ||
      (activeTab === 'Reports' && a?.verified);
    return matchSev && matchSearch && matchTab;
  });

  const criticalCount = alerts.filter(a => a.severity === 'Critical').length;
  const highCount = alerts.filter(a => a.severity === 'High').length;

  if (showReportForm) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowReportForm(false)}
            className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            ←
          </button>
          <h1 className="text-lg font-bold text-foreground">Report a Scam</h1>
        </div>
        <ReportScamForm onClose={() => { setShowReportForm(false); fetchAlerts(); }} />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4">
      {showSignInPrompt && (
        <SignInPromptModal
          onClose={() => setShowSignInPrompt(false)}
          action="report a scam"
        />
      )}

      {/* ─── Hero Header ─── */}
      <div className="relative overflow-hidden rounded-2xl p-5"
        style={{
          background: 'linear-gradient(135deg, rgba(220,38,38,0.18) 0%, rgba(239,68,68,0.08) 50%, rgba(11,18,37,0.9) 100%)',
          border: '1px solid rgba(239,68,68,0.2)',
          boxShadow: '0 4px 32px rgba(239,68,68,0.1)',
        }}
      >
        {/* Background glow */}
        <div className="absolute top-0 right-0 w-48 h-48 blob-red opacity-50 pointer-events-none" />

        <div className="relative flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-xl icon-container-red flex items-center justify-center">
                <ShieldExclamationIcon style={{ width: 17, height: 17 }} className="text-red-400" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="animate-pulse-red w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">Live Monitoring</span>
              </div>
            </div>
            <h1 className="text-xl font-bold text-foreground mb-1">Scam Alert Center</h1>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Community-powered scam detection. Stay informed, stay safe.
            </p>
          </div>
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <div className="text-center px-3 py-2 rounded-xl bg-red-500/15 border border-red-500/20">
              <p className="text-lg font-bold text-red-400">{criticalCount}</p>
              <p className="text-[9px] text-red-400/70 font-semibold uppercase tracking-wide">Critical</p>
            </div>
            <div className="text-center px-3 py-2 rounded-xl bg-orange-500/12 border border-orange-500/20">
              <p className="text-lg font-bold text-orange-400">{highCount}</p>
              <p className="text-[9px] text-orange-400/70 font-semibold uppercase tracking-wide">High</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Filters */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none p-1 bg-muted/40 rounded-xl border border-border/50">
        {tabFilters.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-shrink-0 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
              activeTab === tab
                ? 'bg-card text-foreground shadow-sm border border-border/80'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          style={{ width: 15, height: 15 }}
        />
        <input
          type="text"
          placeholder="Search alerts by project name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-card border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all duration-200"
        />
      </div>

      {/* Severity Pills */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-none">
        {severityFilters.map((sev) => (
          <button
            key={sev}
            onClick={() => setSeverity(sev)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
              severity === sev
                ? sev === 'Critical' ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-sm shadow-red-500/30'
                  : sev === 'High' ? 'bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm shadow-orange-500/30'
                  : sev === 'Medium' ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black shadow-sm'
                  : 'bg-gradient-to-r from-blue-600 to-primary text-white shadow-sm shadow-primary/30' :'bg-muted/60 text-muted-foreground hover:text-foreground border border-border/50'
            }`}
          >
            {sev}
          </button>
        ))}
        <span className="text-muted-foreground text-xs ml-auto flex-shrink-0 font-medium">
          {filtered?.length} alert{filtered?.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Alert Feed */}
      <div className="space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-4">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-xl animate-shimmer flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 animate-shimmer rounded-lg w-1/2" />
                    <div className="h-3 animate-shimmer rounded-lg w-3/4" />
                    <div className="h-3 animate-shimmer rounded-lg w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filtered?.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-10 flex flex-col items-center gap-3 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <FunnelIcon className="text-muted-foreground" style={{ width: 24, height: 24 }} />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground mb-1">No alerts match your filters</p>
              <p className="text-muted-foreground text-xs">Try adjusting your search or filters above</p>
            </div>
          </div>
        ) : (
          filtered?.map((alert) => (
            <AlertCard key={alert?.id} alert={alert} />
          ))
        )}
      </div>

      {/* Report a Scam Button */}
      <div className="sticky bottom-4 pt-2">
        <button
          onClick={handleReportClick}
          className="w-full py-4 rounded-2xl text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all duration-200"
          style={{
            background: 'linear-gradient(135deg, #991b1b 0%, #dc2626 60%, #ef4444 100%)',
            boxShadow: '0 4px 24px rgba(220,38,38,0.4), 0 1px 4px rgba(0,0,0,0.3)',
          }}
        >
          <ShieldExclamationIcon style={{ width: 18, height: 18 }} />
          Report a Scam
        </button>
      </div>
    </div>
  );
}
